import React from 'react';
import { useComputed, useSignalEffect } from '@preact/signals-react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'motion/react';
import { useSignals } from '@preact/signals-react/runtime';
import { IoSearchOutline } from 'react-icons/io5';
import { useAiComponentContext } from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { AiComponentVisualManifest } from '@/pkg/ai';
import { CompoundChange } from '@/pkg/sdk';
import { AiComponentInputCard } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/input-card';
import { BackdropSpinner } from '@/components/backdrop-spinner';
import { toGroupName } from '@/features/editor/widgets/shared/ai-editing-layout/utils/group-name';
import { VSpace } from '@/components/spacing';
import { useManifestSearch } from '@/features/editor/widgets/shared/ai-editing-layout/hooks/use-manifest-search';

export function AiComponentVisualEdit({
  logicOnly = false,
}: {
  logicOnly?: boolean;
}) {
  useSignals();
  const newChange = useAiComponentContext((ctx) => ctx.newChange);
  const highlightedGroup = useAiComponentContext(
    (ctx) => ctx.highlightedVisualGroup,
  );
  const isProcessingManifest = useAiComponentContext(
    (ctx) => ctx.isProcessingVisualManifest,
  );

  const manifest = useComputed(() => {
    const block = newChange.value.block.value as CompoundChange;
    if (block && block.isAiComponent) {
      return block.editManifest as AiComponentVisualManifest;
    }
    return { components: [] } as AiComponentVisualManifest;
  });

  const { searchQuery, filteredComponents } = useManifestSearch(
    manifest,
    logicOnly,
  );

  const hasManifest = useComputed(
    () => filteredComponents.value && filteredComponents.value.length > 0,
  );

  useSignalEffect(() => {
    if (highlightedGroup.value) {
      const highlightElement = (
        selector: string,
        highlightColor = `#f964e7`,
        resetColor = `black`,
        delay = 250,
        duration = 2000,
      ) => {
        const elem = document.querySelector(selector) as HTMLElement;
        if (elem) {
          setTimeout(() => {
            elem.scrollIntoView({
              behavior: `smooth`,
              block: `start`,
              inline: `center`,
            });
            elem.style.color = highlightColor;
            setTimeout(() => {
              elem.style.color = resetColor;
            }, duration);
          }, delay);
          return true;
        }
        return false;
      };

      const groupSelector = `.vsly-group-${toGroupName(
        highlightedGroup.value?.group,
      )}`;
      const selectorSelector = `.vsly-comp-selector-${toGroupName(
        highlightedGroup.value?.selector,
      )}`;

      if (!highlightElement(groupSelector)) {
        highlightElement(selectorSelector);
      }

      const group = highlightedGroup.value?.group;
      if (group) {
        window.dispatchEvent(
          new CustomEvent(`ai-expand-block`, {
            detail: { group },
          }),
        );
      }

      highlightedGroup.value = undefined;
    }
  });

  return (
    <Wrapper
      className="visual-edit-tab"
      key={isProcessingManifest.value ? 1 : 0}
    >
      {isProcessingManifest.value && (
        <BackdropSpinner
          marginTop="-5rem"
          loading
          blurAmount={0}
          spinnerSize={40}
          backgroundColor="rgba(0,0,0,0)"
        />
      )}
      {!logicOnly && (
        <SearchWrapper>
          <SearchTitle>All Elements</SearchTitle>
          <SearchInputWrapper>
            <SearchInput
              placeholder="Search"
              value={searchQuery.value}
              onChange={(e) => {
                searchQuery.value = e.target.value;
              }}
            />
            <IoSearchOutline size={18} color="#A7B2BE" />
          </SearchInputWrapper>
        </SearchWrapper>
      )}
      {!hasManifest.value && !isProcessingManifest.value && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ease: `linear`, duration: 0.2 }}
        >
          {logicOnly
            ? `No logic variables detected.`
            : `No visual components detected in the HTML.`}
        </motion.div>
      )}
      <AnimatePresence mode="popLayout">
        {logicOnly && <VSpace value={1} />}
        {hasManifest.value &&
          !isProcessingManifest.value &&
          filteredComponents.value.map((comp, index) => (
            <AiComponentInputCard
              key={comp.name + comp.type + comp.selector}
              component={comp}
              removeGroupName={!logicOnly}
              expandedAsDefault={logicOnly}
              hideSeparators={logicOnly}
              isFirstInGroup={
                !filteredComponents.value[index - 1] ||
                filteredComponents.value[index - 1].group !== comp.group
              }
            />
          ))}
      </AnimatePresence>
      <VSpace value={10} />
    </Wrapper>
  );
}

const Wrapper = styled(motion.div).attrs({
  layout: true,
  transition: { ease: `linear`, duration: 0.2 },
})`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-y: scroll;
  height: 100%;

  padding-top: 1.5rem;
  padding-bottom: 8rem;

  scroll-padding-top: 1rem;

  mask-image: linear-gradient(
    var(--mask-direction, to bottom),
    hsl(0 0% 0% / 0),
    hsl(0 0% 0% / 1) 3%,
    hsl(0 0% 0% / 1) 95%,
    hsl(0 0% 0% / 0)
  );
`;

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.3rem;
  margin-top: 2rem;
  margin-bottom: 0rem;
`;

const SearchTitle = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: #a7b2be;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Helvetica, serif;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const SearchInput = styled.input`
  border: none;
  background: transparent;
  outline: none;
  font-size: 1.2rem;
  font-family: inherit;
  text-align: right;
  width: 100px;
  font-weight: 600;
  color: #a7b2be;

  &::placeholder {
    color: #a7b2be;
    opacity: 1;
  }
`;
